import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Add from '@mui/icons-material/Add';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import { TabPane } from './TabPane';
import React from 'react';
import { Field, FieldArray } from 'redux-form';
import ZuulQueryBuilder from '../QueryBuilder/ZuulQueryBuilder';
import { WrappedFieldArrayProps } from 'redux-form/lib/FieldArray';
import { PercentageField } from './index';
import CollapsibleCard from '../CollapsibleCard/CollapsibleCard';
import { randomFailureFields } from '../../utils/queryBuilderFields';
import { useDomains } from '../../views/OfferBlocks/queries';
import { useSites } from '../../views/Sites/queries';
import { useAffiliates } from '../../views/Affiliates/queries';
import ZuulLoader from '../ZuulLoader/ZuulLoader';
import { Field as QueryField } from 'react-querybuilder';
import { Entity } from '../../views/CustomProperties/EntityGroups/details/types';
import { useLandingPagesDtos } from '../../views/LandingPages/queries';
import { useLandingPageGroupsDtos } from '../../views/LandingPageGroups/queries';

type RandomFailureProbability = {
  probability: number;
  condition?: string;
};

const AnalysisPane = () => {
  return (
    <TabPane tabId='Analysis'>
      <RandomFailureProbabilities />
    </TabPane>
  );
};

const RandomFailureProbabilities = () => {
  return <FieldArray name='randomFailureProbabilities' component={renderRandomFailureProbabilities} />;
};

const renderRandomFailureProbabilities = ({ fields }: WrappedFieldArrayProps<RandomFailureProbability>) => {
  const { data: domains = [], isLoading: loadingDomains } = useDomains(
    true,
    (data: { id: any; name: any }[]): Entity[] =>
      data.map((domain: { id: any; name: any }) => ({
        id: domain.id,
        name: domain.name,
      }))
  );

  const { data: sites = [], isLoading: loadingSites } = useSites((data): Entity[] =>
    data.map((site: { id: any; domain: any }) => ({
      id: site.id,
      name: site.domain,
    }))
  );

  const { data: affiliates = [], isLoading: loadingAffiliates } = useAffiliates();

  const { data: landingPages = [], isLoading: loadingLandingPages } = useLandingPagesDtos((data): Entity[] =>
    data.map((landingPage: any) => ({
      id: landingPage.id,
      name: landingPage.offer.name,
    }))
  );

  const { data: landingPageGroups = [], isLoading: loadingLandingPageGroups } = useLandingPageGroupsDtos(
    (data): Entity[] =>
      data.map((landingPageGroup: { id: any; name: any }) => ({
        id: landingPageGroup.id,
        name: landingPageGroup.name,
      }))
  );

  if (loadingDomains || loadingSites || loadingAffiliates || loadingLandingPages || loadingLandingPageGroups) {
    return <ZuulLoader text={'Loading Query Builder Fields...'} />;
  }

  return (
    <CollapsibleCard
      title={'Random Failure Probabilities'}
      defaultOpen={true}
      headerButton={
        <Tooltip title='Add a failure probability'>
          <IconButton
            color='success'
            aria-label='Add a failure probability'
            onClick={() => fields.push({ probability: 0 })}
          >
            <Add />
          </IconButton>
        </Tooltip>
      }
    >
      <Stack spacing={2}>
        {fields.map((name: string, index: number) => (
          <Card key={index} variant='outlined'>
            <CardHeader
              title={
                <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                  <Grid size={{ xs: 12, lg: 4 }}>
                    <Field
                      name={`${name}.probability`}
                      component={PercentageField}
                      label='Failure Probability Percentage:'
                      inlineLabel
                    />
                  </Grid>
                  <Grid size='grow' sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Tooltip title='Remove this failure probability'>
                      <IconButton
                        color='error'
                        aria-label='Remove this failure probability'
                        onClick={() => fields.remove(index)}
                      >
                        <DeleteOutlined />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              }
              slotProps={{ title: { component: 'div' } }}
            />
            <CardContent>
              <RandomFailureProbability
                name={name}
                queryFields={randomFailureFields(domains, affiliates, sites, landingPages, landingPageGroups)}
              />
            </CardContent>
          </Card>
        ))}
      </Stack>
    </CollapsibleCard>
  );
};

export interface RandomFailureProbabilityProps {
  name: string;
  queryFields: QueryField[];
}

const RandomFailureProbability: React.FC<RandomFailureProbabilityProps> = ({ name, queryFields }) => (
  <Field
    name={`${name}.condition`}
    component={({ input }: { input: { value: any; onChange: (value: any) => void } }) => (
      <ZuulQueryBuilder query={input.value} fields={queryFields} setQuery={input.onChange} />
    )}
    label={'Condition'}
  />
);

export default AnalysisPane;
